import { filter, findIndex, includes, map, reduce, sortBy, uniq, values } from 'lodash';
import { useMemo, useSyncExternalStore } from 'react';

import { getStoredPreferenceValue, setStoredPreferenceValue } from 'utils/userPreferences';

export const PROCEDURE_PAGE_CARDS_PER_ROW_STORAGE_KEY = 'procedurePageCardsPerRow';
export const LEGACY_LARGE_SCREEN_CASES_PER_ROW_STORAGE_KEY = 'procedurePageLargeCasesPerRow';

export const SUPPORTED_CASES_PER_ROW_OPTIONS = [1, 2, 3, 4, 6] as const;

const DEFAULT_SMALL_SCREEN_CASES_PER_ROW = 1;
const DEFAULT_CARDS_PER_ROW = 4;
const DEFAULT_PAGE_SIZE_OPTIONS = [12, 24, 36, 60, 96];
const PROCEDURE_PAGE_LAYOUT_STORAGE_EVENT = 'procedurePageLayoutStorageChange';

type ProcedurePageScreenLayout = 'large' | 'medium';

const normalizeCasesPerRow = (value: number, allowedValues: readonly number[], fallbackValue: number) =>
  includes(allowedValues, value) ? value : fallbackValue;

const readStoredCardsPerRow = () => {
  const storedCardsPerRow = Number(getStoredPreferenceValue(PROCEDURE_PAGE_CARDS_PER_ROW_STORAGE_KEY));

  if (includes(SUPPORTED_CASES_PER_ROW_OPTIONS, storedCardsPerRow)) {
    return storedCardsPerRow;
  }

  const legacyLargeCasesPerRow = Number(getStoredPreferenceValue(LEGACY_LARGE_SCREEN_CASES_PER_ROW_STORAGE_KEY));

  return normalizeCasesPerRow(legacyLargeCasesPerRow, SUPPORTED_CASES_PER_ROW_OPTIONS, DEFAULT_CARDS_PER_ROW);
};

const subscribeToStoredCasesPerRow = (storageKey: string, onStoreChange: () => void): (() => void) => {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const handleStorage = (event: StorageEvent) => {
    if (event.key === storageKey) {
      onStoreChange();
    }
  };

  const handleProcedurePageLayoutStorageChange = (event: Event) => {
    const procedurePageLayoutStorageEvent = event as CustomEvent<{ storageKey?: string }>;

    if (procedurePageLayoutStorageEvent.detail?.storageKey === storageKey) {
      onStoreChange();
    }
  };

  window.addEventListener('storage', handleStorage);
  window.addEventListener(PROCEDURE_PAGE_LAYOUT_STORAGE_EVENT, handleProcedurePageLayoutStorageChange);

  return () => {
    window.removeEventListener('storage', handleStorage);
    window.removeEventListener(PROCEDURE_PAGE_LAYOUT_STORAGE_EVENT, handleProcedurePageLayoutStorageChange);
  };
};

const setStoredCasesPerRow = (storageKey: string, value: number) => {
  setStoredPreferenceValue(storageKey, value.toString());

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(PROCEDURE_PAGE_LAYOUT_STORAGE_EVENT, {
        detail: { storageKey },
      })
    );
  }
};

const getGreatestCommonDivisor = (left: number, right: number): number => {
  if (right === 0) {
    return left;
  }

  return getGreatestCommonDivisor(right, left % right);
};

const getLeastCommonMultiple = (left: number, right: number) =>
  Math.abs(left * right) / getGreatestCommonDivisor(left, right);

const getProcedurePageSizeBase = (casesPerRowCounts: number[]) =>
  reduce(
    casesPerRowCounts,
    (currentBase, casesPerRowCount) => getLeastCommonMultiple(currentBase, casesPerRowCount),
    1
  );

const getSupportedCasesPerRowOption = (casesPerRow: number, fallbackValue: number) => {
  const casesPerRowOptionIndex = findIndex(SUPPORTED_CASES_PER_ROW_OPTIONS, (option) => option === casesPerRow);

  if (casesPerRowOptionIndex < 0) {
    return fallbackValue;
  }

  return SUPPORTED_CASES_PER_ROW_OPTIONS[casesPerRowOptionIndex];
};

export const getProcedurePageCardsPerRow = ({
  cardsPerRow,
  layout,
}: {
  cardsPerRow: number;
  layout: ProcedurePageScreenLayout;
}) => {
  const normalizedCardsPerRow = normalizeCasesPerRow(
    cardsPerRow,
    SUPPORTED_CASES_PER_ROW_OPTIONS,
    DEFAULT_CARDS_PER_ROW
  );
  const cardsPerRowOption = getSupportedCasesPerRowOption(normalizedCardsPerRow, DEFAULT_CARDS_PER_ROW);
  const cardsPerRowOptionIndex = findIndex(SUPPORTED_CASES_PER_ROW_OPTIONS, (option) => option === cardsPerRowOption);
  const shouldKeepDenseLargeLayoutAtMd = layout === 'large' && cardsPerRowOption > DEFAULT_CARDS_PER_ROW;
  const mediumCasesPerRowIndex = shouldKeepDenseLargeLayoutAtMd
    ? cardsPerRowOptionIndex
    : Math.max(0, cardsPerRowOptionIndex - (layout === 'medium' ? 2 : 1));

  return {
    xs: DEFAULT_SMALL_SCREEN_CASES_PER_ROW,
    sm: DEFAULT_SMALL_SCREEN_CASES_PER_ROW,
    md: SUPPORTED_CASES_PER_ROW_OPTIONS[mediumCasesPerRowIndex],
    lg: normalizedCardsPerRow,
  };
};

export const getProcedurePageSizeOptions = ({ cardsPerRow }: { cardsPerRow: number }) => {
  const matchingCasesPerRowCounts = uniq([
    ...values(
      getProcedurePageCardsPerRow({
        cardsPerRow,
        layout: 'large',
      })
    ),
    ...values(
      getProcedurePageCardsPerRow({
        cardsPerRow,
        layout: 'medium',
      })
    ),
  ]);
  const pageSizeBase = getProcedurePageSizeBase(matchingCasesPerRowCounts);

  return sortBy(
    uniq(
      filter(
        [...map([1, 2, 3], (multiplier) => pageSizeBase * multiplier), ...DEFAULT_PAGE_SIZE_OPTIONS],
        (pageSize) => {
          return pageSize % pageSizeBase === 0;
        }
      )
    )
  );
};

export const useProcedurePageLayout = () => {
  const storedCardsPerRow = useSyncExternalStore(
    (onStoreChange) => subscribeToStoredCasesPerRow(PROCEDURE_PAGE_CARDS_PER_ROW_STORAGE_KEY, onStoreChange),
    readStoredCardsPerRow,
    () => DEFAULT_CARDS_PER_ROW
  );

  const cardsPerRow = normalizeCasesPerRow(storedCardsPerRow, SUPPORTED_CASES_PER_ROW_OPTIONS, DEFAULT_CARDS_PER_ROW);

  const pageSizeOptions = useMemo(() => getProcedurePageSizeOptions({ cardsPerRow }), [cardsPerRow]);

  return {
    cardsPerRow,
    cardsPerRowOptions: filter(SUPPORTED_CASES_PER_ROW_OPTIONS, Boolean) as number[],
    pageSizeOptions,
    setCardsPerRow: (nextCasesPerRow: number) => {
      setStoredCasesPerRow(
        PROCEDURE_PAGE_CARDS_PER_ROW_STORAGE_KEY,
        normalizeCasesPerRow(nextCasesPerRow, SUPPORTED_CASES_PER_ROW_OPTIONS, cardsPerRow)
      );
    },
  };
};
